// The quick lines, the pure half (ui-map-interface.md §8 "Quick chat",
// R1607, R1608; mockup round 077, cards 01, 04 and 05).
//
// A quick line is one of the twelve fixed lines of
// data/contracts/quickchat.toml, sent with one gesture to the SQUAD or the
// FACTION channel. The wire carries the NUMBER of the line (1 for the
// first row) and the position the hub resolved, and never the words: this
// module writes the line from the reader's own locale table, so a German
// reader reads an English sender's line in German (card 05).
//
// Two questions live here and nowhere else, so the wheel, the chat log and
// the tests read one answer:
//
// - What a line names for its SENDER (`quickRef`): the first entry of the
//   line's `carries` list that the sender has, or `null` when the sender
//   has none of it. A null line greys out on the wheel and sends nothing.
// - What a line's position READS (`posText`): the ⌖ pill of the chat log
//   and the line under the wheel.

import { QUICK_LINES } from '../gen/balance';
import { QuickRefKind, type QuickPos } from '../gen/proto/aquaphobia';
import { quickchat as S } from '../gen/strings';
import { cellPos } from './chartpos';
import { wordOf } from '../locale/wordof';

/** The code of one quick line. */
export type QuickCode = (typeof QUICK_LINES)[number]['code'];
/** What a quick line may name, as the TOML spells it. */
export type QuickCarry = (typeof QUICK_LINES)[number]['carries'][number];

/** How many quick lines there are: the sectors of the wheel. */
export const QUICK_COUNT = QUICK_LINES.length;

/** The wire kind of each `carries` word (R1608). */
const KIND: Record<QuickCarry, QuickRefKind> = {
  marker: QuickRefKind.QUICK_REF_KIND_MARKER,
  hold: QuickRefKind.QUICK_REF_KIND_HOLD,
  fleet: QuickRefKind.QUICK_REF_KIND_FLEET,
  contact: QuickRefKind.QUICK_REF_KIND_CONTACT,
  object: QuickRefKind.QUICK_REF_KIND_OBJECT,
};

/** The code of line n (1-based), or null for a number the table lacks. */
export function quickCode(n: number): QuickCode | null {
  return Number.isInteger(n) && n >= 1 && n <= QUICK_COUNT ? QUICK_LINES[n - 1].code : null;
}

/** The number (1-based) of the line with one code. RALLY sends `siege`. */
export function quickLineOf(code: QuickCode): number {
  return QUICK_LINES.findIndex((l) => l.code === code) + 1;
}

/** Line n in the reader's language, or '' for a number the table lacks. */
export function quickText(n: number): string {
  const code = quickCode(n);
  return code === null ? '' : S[`line_${code}`];
}

/** The word of line n on its sector of the wheel. */
export function quickWord(n: number): string {
  const code = quickCode(n);
  return code === null ? '' : S[`word_${code}`];
}

/** What a greyed line needs, or '' for a line that names nothing. */
export function quickNeeds(n: number): string {
  const code = quickCode(n);
  return code === null ? '' : wordOf(S, `needs_${code}`, '');
}

/**
 * What the sender's client can name right now. Each field is null when the
 * sender has none of it. The client reads its own state; the hub resolves
 * the position again from its own, so this is a preview and a gate, never
 * the truth (R1608).
 */
export interface QuickCtx {
  /** The sender's newest live map marker. */
  marker: { cell: number } | null;
  /** The sender's own hold. */
  hold: { siteId: number; cell: number; name: string } | null;
  /** The selected own fleet. */
  fleet: { id: number; cell: number; name: string } | null;
  /** The selected fleet of another faction. */
  contact: { id: number; cell: number } | null;
  /** The strategic object on the cell of the newest marker, with the
   *  world-clock hour of its window. */
  object: { siteId: number; cell: number; name: string; hour: number } | null;
}

/** An empty context: the sender can name nothing. */
export const NO_QUICK_CTX: QuickCtx = { marker: null, hold: null, fleet: null, contact: null, object: null };

/** What a line names: the reference the wire carries and the position it
 *  will read, as a QuickPos the hub would send back. */
export interface QuickPick {
  kind: QuickRefKind;
  id: number;
  pos: QuickPos | null;
}

/**
 * What line n names from the sender's context, in the order of its
 * `carries` list; a line that names nothing answers the NONE reference.
 * It answers null when the line names something and the sender has none
 * of it: the line greys out and is not sent.
 */
export function quickRef(n: number, ctx: QuickCtx): QuickPick | null {
  if (quickCode(n) === null) return null;
  const carries: readonly QuickCarry[] = QUICK_LINES[n - 1].carries;
  if (carries.length === 0) return { kind: QuickRefKind.QUICK_REF_KIND_NONE, id: 0, pos: null };
  for (const c of carries) {
    const kind = KIND[c];
    const pos = (cell: number, siteId = 0, fleetName = ''): QuickPick['pos'] =>
      ({ kind, cell, siteId, fleetName });
    switch (c) {
      case 'marker':
        if (ctx.marker) return { kind, id: 0, pos: pos(ctx.marker.cell) };
        break;
      case 'hold':
        if (ctx.hold) return { kind, id: 0, pos: pos(ctx.hold.cell, ctx.hold.siteId) };
        break;
      case 'fleet':
        if (ctx.fleet) return { kind, id: ctx.fleet.id, pos: pos(ctx.fleet.cell, 0, ctx.fleet.name) };
        break;
      case 'contact':
        if (ctx.contact) return { kind, id: ctx.contact.id, pos: pos(ctx.contact.cell) };
        break;
      case 'object':
        if (ctx.object) return { kind, id: ctx.object.siteId, pos: pos(ctx.object.cell, ctx.object.siteId) };
        break;
    }
  }
  return null;
}

/** What a reader's client knows to name a position: the chart position
 *  the graticule labels (R2742), the name of a site, and the window hour
 *  of an object. */
export interface PosLookup {
  pos(cell: number): string;
  siteName(siteId: number): string | undefined;
  windowHour(siteId: number): number | undefined;
}

/** What the lookup reads of the state store. `GameState` satisfies it. */
export interface PosSource {
  readonly sites: ReadonlyMap<number, { name: string }>;
  readonly sieges: readonly { siteId: number; siteName: string; windowHour: number }[];
}

/**
 * The lookup of one client: the cell figures of the live grid, the name of
 * a site the client holds, and the window hour of a strategic object from
 * the public siege frame. The store is read at each call, so a site that
 * arrives later names its line at the next render.
 */
export function posLookupOf(src: () => PosSource): PosLookup {
  return {
    pos: (cell) => cellPos(cell),
    siteName: (id) => src().sites.get(id)?.name
      ?? src().sieges.find((s) => s.siteId === id)?.siteName,
    windowHour: (id) => src().sieges.find((s) => s.siteId === id)?.windowHour,
  };
}

/**
 * What one position reads in the reader's language (card 04): ⌖ and the
 * cell figures for a marker and a contact, ⌖ and the name for a hold and an
 * own fleet, and the window hour and the name for the object of line 5. A
 * name the reader's client lacks falls back on the cell. A cell name, a
 * hold name and a hull name stay as they are in every language (card 05).
 */
export function posText(pos: QuickPos, look: PosLookup): string {
  const cell = (): string => S.pos_cell({ pos: look.pos(pos.cell) });
  switch (pos.kind) {
    case QuickRefKind.QUICK_REF_KIND_HOLD: {
      const name = look.siteName(pos.siteId);
      return name ? S.pos_name({ name }) : cell();
    }
    case QuickRefKind.QUICK_REF_KIND_FLEET:
      return pos.fleetName ? S.pos_name({ name: pos.fleetName }) : cell();
    case QuickRefKind.QUICK_REF_KIND_OBJECT: {
      const name = look.siteName(pos.siteId);
      const hour = look.windowHour(pos.siteId);
      if (name && hour !== undefined) return S.pos_siege({ hour: String(hour).padStart(2, '0'), name });
      return name ? S.pos_name({ name }) : cell();
    }
    default:
      return cell();
  }
}
